import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import toast from 'react-hot-toast';

const ROLES = ['gerencia', 'administracion', 'produccion', 'punto_venta', 'empleado'];
const ROLES_LABEL = { gerencia:'Gerencia', administracion:'Administración', produccion:'Producción', punto_venta:'Punto venta', empleado:'Empleado' };

const BADGE = {
  gerencia:       { bg: '#EFF6FF', color: '#1D4ED8' },
  administracion: { bg: '#F0FDF4', color: '#065F46' },
  produccion:     { bg: '#FEF3C7', color: '#92400E' },
  punto_venta:    { bg: '#FDF4FF', color: '#7E22CE' },
  empleado:       { bg: '#F3F4F6', color: '#374151' },
};

const DIAS_SEMANA = [
  { key: 'lun', label: 'Lun' }, { key: 'mar', label: 'Mar' }, { key: 'mie', label: 'Mié' },
  { key: 'jue', label: 'Jue' }, { key: 'vie', label: 'Vie' }, { key: 'sab', label: 'Sáb' }, { key: 'dom', label: 'Dom' },
];
const CANAL_OPCIONES = [{ v:'ambos', l:'📱 Push + SMS' }, { v:'push', l:'📱 Solo push' }, { v:'sms', l:'💬 Solo SMS' }];
const st = { input: { padding:'7px 10px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', width:'100%', boxSizing:'border-box' } };

// Muestra "08:00" como "8:00 AM" en la vista resumida
const formatearHora = (hora24) => {
  if (!hora24) return '';
  const [h, m] = hora24.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2,'0')} ${ampm}`;
};
const formatearDias = (diasStr) => {
  if (!diasStr) return '';
  const dias = diasStr.split(',').map(d => d.trim());
  if (dias.length === 7) return 'Todos los días';
  const labelMap = Object.fromEntries(DIAS_SEMANA.map(d => [d.key, d.label]));
  return dias.map(d => labelMap[d] || d).join(', ');
};

// ── Tab Permisos ──────────────────────────────────────────
function TabPermisos() {
  const { puedeVer } = useAuth();
  const [modulos, setModulos]       = useState([]);
  const [cambios, setCambios]       = useState({});
  const [guardando, setGuardando]   = useState(null);
  const [guardadoOk, setGuardadoOk] = useState({});

  useEffect(() => {
    API.get('/modulos/permisos')
      .then(r => setModulos(r.data))
      .catch(() => toast.error('Error cargando permisos'));
  }, []);

  if (!puedeVer('gerencia')) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Solo gerencia puede ver esta sección.</div>;
  }

  const getRoles = (modulo) => {
    return cambios[modulo.path] ?? modulo.roles ?? [];
  };

  const toggleRol = (path, rol) => {
    const actual = cambios[path] ?? modulos.find(m => m.path === path)?.roles ?? [];
    const nuevo  = actual.includes(rol) ? actual.filter(r => r !== rol) : [...actual, rol];
    setCambios(c => ({ ...c, [path]: nuevo }));
    setGuardadoOk(g => ({ ...g, [path]: false }));
  };

  const guardar = async (modulo) => {
    setGuardando(modulo.path);
    try {
      const roles = getRoles(modulo);
      const pathKey = modulo.path.replace(/^\//, '');
      await API.put(`/modulos/permisos/${pathKey}`, { roles });
      // Actualizar el módulo local
      setModulos(ms => ms.map(m => m.path === modulo.path ? { ...m, roles } : m));
      setCambios(c => { const n = {...c}; delete n[modulo.path]; return n; });
      setGuardadoOk(g => ({ ...g, [modulo.path]: true }));
      toast.success(`✅ ${modulo.nombre} actualizado`);
      setTimeout(() => setGuardadoOk(g => ({ ...g, [modulo.path]: false })), 2000);
    } catch { toast.error('Error al guardar'); }
    finally { setGuardando(null); }
  };

  const tieneCambios = (path) => !!cambios[path];

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>🔐 Permisos de módulos</h1>
        <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14 }}>
          Define qué roles tienen acceso a cada módulo del ERP. Los cambios se aplican inmediatamente.
        </p>
      </div>

      {/* Leyenda de roles */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {ROLES.map(r => (
          <span key={r} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: BADGE[r].bg, color: BADGE[r].color }}>
            {r}
          </span>
        ))}
      </div>

      {/* Tabla de módulos */}
      <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#1a1a2e' }}>
              <th style={{ padding: '12px 18px', textAlign: 'left', color: 'white', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', width: '35%' }}>Módulo</th>
              {ROLES.map(r => (
                <th key={r} style={{ padding: '12px 10px', textAlign: 'center', color: 'white', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{r}</th>
              ))}
              <th style={{ padding: '12px 10px', textAlign: 'center', color: 'white', fontSize: 11, fontWeight: 700, width: 80 }}>Guardar</th>
            </tr>
          </thead>
          <tbody>
            {modulos.map((m, i) => {
              const roles = getRoles(m);
              const cambio = tieneCambios(m.path);
              const ok     = guardadoOk[m.path];
              return (
                <tr key={m.path} style={{ borderTop: '1px solid #F3F4F6', background: cambio ? '#FFFBEB' : i%2===0 ? 'white' : '#FAFAFA' }}>
                  <td style={{ padding: '12px 18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 16 }}>{m.icon}</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>{m.nombre}</div>
                        <div style={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'monospace' }}>{m.path}</div>
                      </div>
                    </div>
                  </td>
                  {ROLES.map(r => (
                    <td key={r} style={{ padding: '12px 10px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={roles.includes(r)}
                        onChange={() => toggleRol(m.path, r)}
                        style={{ width: 18, height: 18, cursor: 'pointer', accentColor: BADGE[r].color }}
                      />
                    </td>
                  ))}
                  <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                    {ok ? (
                      <span style={{ color: '#059669', fontSize: 18 }}>✅</span>
                    ) : (
                      <button
                        onClick={() => guardar(m)}
                        disabled={!cambio || guardando === m.path}
                        style={{
                          padding: '5px 12px', border: 'none', borderRadius: 6, cursor: cambio ? 'pointer' : 'default',
                          background: cambio ? '#2563EB' : '#E5E7EB', color: cambio ? 'white' : '#9CA3AF',
                          fontSize: 12, fontWeight: 700, transition: 'all 0.15s'
                        }}>
                        {guardando === m.path ? '⏳' : 'OK'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 16, padding: '12px 16px', background: '#FEF3C7', borderRadius: 10, fontSize: 13, color: '#92400E' }}>
        ⚠️ Los usuarios necesitan <strong>cerrar sesión y volver a entrar</strong> para ver los cambios aplicados en su menú.
      </div>
    </div>
  );
}

// ── Tab Agente ────────────────────────────────────────────
function TabAgente() {
  const [alertas, setAlertas]   = useState([]);
  const [editando, setEditando] = useState(null);
  const [form, setForm]         = useState({});
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    API.get('/agente/config')
      .then(r => setAlertas(r.data))
      .catch(() => toast.error('Error cargando configuración del agente'));
  }, []);

  const abrirEditar = (a) => {
    setEditando(a.alerta_id);
    setForm({
      activa: a.activa,
      hora_local: a.hora_local || '08:00',
      dias_semana: a.dias_semana ? a.dias_semana.split(',').map(d => d.trim()) : ['lun','mar','mie','jue','vie','sab','dom'],
      roles_destinatarios: a.roles_destinatarios || [],
      canal: a.canal || 'ambos',
      umbral: a.umbral || '',
    });
  };

  const toggleDia = (dia) => {
    setForm(f => ({
      ...f,
      dias_semana: f.dias_semana.includes(dia)
        ? f.dias_semana.filter(d => d !== dia)
        : [...f.dias_semana, dia]
    }));
  };

  const toggleRol = (rol) => {
    setForm(f => ({
      ...f,
      roles_destinatarios: f.roles_destinatarios.includes(rol)
        ? f.roles_destinatarios.filter(r => r !== rol)
        : [...f.roles_destinatarios, rol]
    }));
  };

  const guardar = async () => {
    if (form.dias_semana.length === 0) { toast.error('Selecciona al menos un día'); return; }
    setGuardando(true);
    try {
      const payload = { ...form, dias_semana: form.dias_semana.join(',') };
      const r = await API.put(`/agente/config/${editando}`, payload);
      setAlertas(as => as.map(a => a.alerta_id === editando ? r.data : a));
      setEditando(null);
      toast.success('✅ Configuración guardada');
    } catch { toast.error('Error al guardar'); }
    finally { setGuardando(false); }
  };

  const CANAL_BG = { ambos:'#EFF6FF', push:'#F0FDF4', sms:'#FEF3C7' };
  const CANAL_COLOR = { ambos:'#1D4ED8', push:'#065F46', sms:'#92400E' };
  const CANAL_ICON = { ambos:'📱💬', push:'📱', sms:'💬' };

  return (
    <div>
      <div style={{ background:'#F0F9FF', borderRadius:10, padding:'10px 16px', marginBottom:16, fontSize:13, color:'#0369A1', border:'1px solid #BAE6FD' }}>
        🤖 El agente verifica estos parámetros automáticamente. Desactiva una alerta para pausarla sin eliminarla.
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {alertas.map(a => (
          <div key={a.alerta_id} style={{ background:'white', borderRadius:14, padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border: a.activa ? '1px solid #E5E7EB' : '1px solid #FECACA' }}>
            {editando === a.alerta_id ? (
              // Formulario de edición
              <div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                  <span style={{ fontWeight:800, fontSize:15, color:'#111827' }}>{a.nombre}</span>
                  <button onClick={() => setEditando(null)} style={{ background:'none', border:'none', color:'#9CA3AF', fontSize:18, cursor:'pointer' }}>×</button>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:12, marginBottom:14 }}>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Hora de envío</div>
                    <input type="time" value={form.hora_local} onChange={e=>setForm(f=>({...f,hora_local:e.target.value}))} style={{...st.input, maxWidth:160}} />
                  </div>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:8 }}>Días</div>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      {DIAS_SEMANA.map(d => (
                        <button key={d.key} onClick={() => toggleDia(d.key)}
                          style={{ padding:'6px 12px', border:`2px solid ${form.dias_semana.includes(d.key)?'#2563EB':'#E5E7EB'}`, borderRadius:8, background:form.dias_semana.includes(d.key)?'#2563EB':'white', color:form.dias_semana.includes(d.key)?'white':'#374151', cursor:'pointer', fontSize:12, fontWeight:700 }}>
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:8 }}>Canal de envío</div>
                  <div style={{ display:'flex', gap:8 }}>
                    {CANAL_OPCIONES.map(c => (
                      <button key={c.v} onClick={() => setForm(f=>({...f,canal:c.v}))}
                        style={{ padding:'6px 14px', border:`2px solid ${form.canal===c.v?CANAL_COLOR[c.v]:'#E5E7EB'}`, borderRadius:8, background:form.canal===c.v?CANAL_BG[c.v]:'white', color:form.canal===c.v?CANAL_COLOR[c.v]:'#374151', cursor:'pointer', fontSize:12, fontWeight:700 }}>
                        {c.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:8 }}>Roles destinatarios</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {ROLES.map(r => (
                      <label key={r} style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 12px', border:`2px solid ${form.roles_destinatarios.includes(r)?BADGE[r].color:'#E5E7EB'}`, borderRadius:20, cursor:'pointer', background:form.roles_destinatarios.includes(r)?BADGE[r].bg:'white' }}>
                        <input type="checkbox" checked={form.roles_destinatarios.includes(r)} onChange={()=>toggleRol(r)} style={{ display:'none' }} />
                        <span style={{ fontSize:12, fontWeight:700, color:form.roles_destinatarios.includes(r)?BADGE[r].color:'#374151' }}>{ROLES_LABEL[r]}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {a.umbral !== null && (
                  <div style={{ marginBottom:14 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Umbral (días)</div>
                    <input type="number" value={form.umbral} onChange={e=>setForm(f=>({...f,umbral:parseInt(e.target.value)||''}))} style={{...st.input, maxWidth:100}} />
                  </div>
                )}
                <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                  <button onClick={() => setEditando(null)} style={{ padding:'8px 16px', border:'2px solid #E5E7EB', borderRadius:8, background:'white', cursor:'pointer', fontSize:13 }}>Cancelar</button>
                  <button onClick={guardar} disabled={guardando} style={{ padding:'8px 20px', border:'none', borderRadius:8, background:'#2563EB', color:'white', cursor:'pointer', fontSize:13, fontWeight:700 }}>
                    {guardando ? '⏳' : '💾 Guardar'}
                  </button>
                </div>
              </div>
            ) : (
              // Vista resumida
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                    <span style={{ fontWeight:700, fontSize:14, color: a.activa?'#111827':'#9CA3AF' }}>{a.nombre}</span>
                    <span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, fontWeight:700, background:a.activa?'#D1FAE5':'#FEE2E2', color:a.activa?'#065F46':'#991B1B' }}>
                      {a.activa ? 'Activa' : 'Pausada'}
                    </span>
                  </div>
                  <div style={{ fontSize:12, color:'#6B7280' }}>{a.descripcion}</div>
                  <div style={{ display:'flex', gap:10, marginTop:8, flexWrap:'wrap' }}>
                    <span style={{ fontSize:11, color:'#374151', background:'#F3F4F6', padding:'3px 8px', borderRadius:6 }}>🕐 {formatearHora(a.hora_local)}</span>
                    <span style={{ fontSize:11, color:'#374151', background:'#F3F4F6', padding:'3px 8px', borderRadius:6 }}>📅 {formatearDias(a.dias_semana)}</span>
                    <span style={{ fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:6, background:CANAL_BG[a.canal]||'#F3F4F6', color:CANAL_COLOR[a.canal]||'#374151' }}>
                      {CANAL_ICON[a.canal]} {CANAL_OPCIONES.find(c=>c.v===a.canal)?.l||a.canal}
                    </span>
                    {a.umbral && <span style={{ fontSize:11, color:'#374151', background:'#F3F4F6', padding:'3px 8px', borderRadius:6 }}>⚠️ Umbral: {a.umbral} días</span>}
                    {(a.roles_destinatarios||[]).map(r => (
                      <span key={r} style={{ fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:6, background:BADGE[r]?.bg, color:BADGE[r]?.color }}>{ROLES_LABEL[r]}</span>
                    ))}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                  <button onClick={() => {
                    API.put(`/agente/config/${a.alerta_id}`, {...a, activa: !a.activa})
                      .then(r => { setAlertas(as => as.map(x => x.alerta_id===a.alerta_id ? r.data : x)); toast.success(r.data.activa ? 'Alerta activada' : 'Alerta pausada'); })
                      .catch(() => toast.error('Error'));
                  }} style={{ padding:'7px 12px', border:'1px solid #E5E7EB', borderRadius:8, background:'white', cursor:'pointer', fontSize:12, fontWeight:700, color: a.activa?'#EF4444':'#059669' }}>
                    {a.activa ? '⏸ Pausar' : '▶️ Activar'}
                  </button>
                  <button onClick={() => abrirEditar(a)} style={{ padding:'7px 12px', border:'1px solid #E5E7EB', borderRadius:8, background:'white', cursor:'pointer', fontSize:12 }}>
                    ✏️ Editar
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Componente principal con pestañas ─────────────────────
export default function Permisos() {
  const { puedeVer } = useAuth();
  const [tab, setTab] = useState('permisos');

  if (!puedeVer('gerencia')) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Solo gerencia puede ver esta sección.</div>;
  }

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>🔐 Configuración del sistema</h1>
        <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14 }}>Permisos de módulos y configuración del agente de alertas</p>
      </div>

      <div style={{ display:'flex', borderBottom:'2px solid #E5E7EB', marginBottom:20 }}>
        {[{ id:'permisos', label:'🔐 Permisos de módulos' }, { id:'agente', label:'🤖 Agente de alertas' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding:'10px 22px', border:'none', cursor:'pointer', fontSize:13, background:'transparent', fontWeight: tab===t.id?800:500, color: tab===t.id?'#1a1a2e':'#6B7280', borderBottom: tab===t.id?'2px solid #1a1a2e':'2px solid transparent', marginBottom:-2 }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'permisos' && <TabPermisos />}
      {tab === 'agente'   && <TabAgente />}
    </div>
  );
}
